import classNames from '@sindresorhus/class-names';
import {useContext, useRef, useState} from 'react';
import {FieldValues, useFormContext} from 'react-hook-form';

import {CaptchaTokenPayload} from '../types';
import {Captcha} from './Captcha';
import {CaptchaContext} from './CaptchaContext';
import styles from './CaptchaWithSubmitButton.module.scss';

type Props<FormDataType extends FieldValues> = {
  /**
   * before_click - кнопка сабмита дизейблится пока в форме есть невалидные поля
   * after_click - по сабмиту можно кликнуть, после клика сначала будет проверка на валидность
   */
  formValidationMode: 'before_click' | 'after_click';
  /** любой a-z_ текст с намеком на то, где показали этот компонент (для аналитики) */
  placement: string;
  captchaLanguage?: 'en' | 'ru';
  captchaClassName?: string;
  /** запрос на дизейбл формы (используется только когда showCaptchaAfterSubmit=true) */
  onDisableForm: () => void;
  renderSubmitButton: (props: {
    disabled: boolean;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    onResetClick: (event: React.MouseEvent<HTMLElement>) => void;
  }) => React.ReactNode;
  onCaptchaError: () => void;
  onSubmitForm: (data: FormDataType, captcha: CaptchaTokenPayload) => void;
};

const CaptchaAfterSubmitFlow = <FormDataType extends FieldValues>({
  placement,
  captchaLanguage,
  formValidationMode,
  captchaClassName,
  renderSubmitButton,
  onDisableForm,
  onCaptchaError,
  onSubmitForm,
}: Props<FormDataType>) => {
  const {
    trigger,
    getValues,
    formState: {isValid, isDirty},
  } = useFormContext<FormDataType>();
  // после дизейбла формы не получается достать из нее значения, поэтому сохраняем их перед
  const formValuesSnapshotRef = useRef<FormDataType | null>(null);
  const [formCompleted, setFormCompleted] = useState(false);
  const isButtonDisabled =
    formValidationMode === 'before_click' && !(isValid && isDirty);

  if (formCompleted) {
    return (
      <div className={captchaClassName}>
        <Captcha
          language={captchaLanguage ?? 'ru'}
          placement={placement}
          onResult={(captcha) => {
            if (formValuesSnapshotRef.current) {
              onSubmitForm(formValuesSnapshotRef.current, captcha);
            }
          }}
          onError={onCaptchaError}
        />
      </div>
    );
  }

  return renderSubmitButton({
    disabled: isButtonDisabled,
    onClick: (event) => {
      event.preventDefault();
      event.stopPropagation();
      const complete = () => {
        formValuesSnapshotRef.current = getValues();
        onDisableForm();
        setFormCompleted(true);
      };

      if (formValidationMode === 'before_click') {
        complete();
      } else {
        trigger().then((isValid) => {
          if (isValid) {
            complete();
          }
        });
      }
    },
    onResetClick: () => {
      // noop
    },
  });
};

const CaptchaBeforeSubmitFlow = <FormDataType extends FieldValues>({
  placement,
  captchaLanguage,
  formValidationMode,
  captchaClassName,
  renderSubmitButton,
  onCaptchaError,
  onSubmitForm,
}: Props<FormDataType>) => {
  const {
    trigger,
    getValues,
    formState: {isValid, isDirty},
  } = useFormContext<FormDataType>();
  const {provider} = useContext(CaptchaContext);
  const [captchaPayload, setCaptchaPayload] =
    useState<CaptchaTokenPayload | null>(null);
  const [showCaptchaError, setShowCaptchaError] = useState(false);

  const isButtonDisabled =
    formValidationMode === 'before_click' &&
    !((isValid && isDirty) || !captchaPayload?.token);

  return (
    <>
      <div
        className={classNames(captchaClassName, {
          [styles.vk_error]: provider === 'vk' && showCaptchaError,
          [styles.yandex_error]: provider === 'yandex' && showCaptchaError,
        })}
      >
        <Captcha
          placement={placement}
          language={captchaLanguage ?? 'ru'}
          onResult={setCaptchaPayload}
          onError={onCaptchaError}
        />
      </div>
      {renderSubmitButton({
        disabled: isButtonDisabled,
        onClick: (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!captchaPayload?.token) {
            setShowCaptchaError(true);
          }
          if (formValidationMode === 'before_click') {
            // форма уже валидна - можем отправлять
            captchaPayload?.token && onSubmitForm(getValues(), captchaPayload);
          } else {
            trigger().then((isValid) => {
              if (isValid && captchaPayload?.token) {
                onSubmitForm(getValues(), captchaPayload);
              }
            });
          }
        },
        onResetClick: () => {
          setShowCaptchaError(false);
        },
      })}
    </>
  );
};

/**
 * компонент рисует кнопку отправки формы вместе с капчей,
 * или сначала только кнопку, а после клика - капчу
 * (зависит от настройки showCaptchaAfterSubmit контекста)
 */
export const CaptchaWithSubmitButton = <FormDataType extends FieldValues>(
  props: Props<FormDataType>,
) => {
  const {showCaptchaAfterSubmit} = useContext(CaptchaContext);
  if (showCaptchaAfterSubmit) {
    return <CaptchaAfterSubmitFlow {...props} />;
  }
  return <CaptchaBeforeSubmitFlow {...props} />;
};
